import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { getConfirmDeleteDialog } from "./confirmDeleteDialog";
import {
  Alert,
  CssBaseline,
  Box,
  Button,
  Dialog,
  Tooltip,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Snackbar,
  Grid,
  ThemeProvider,
} from "@mui/material";
import { EditOutlined as EditOutlinedIcon } from "@mui/icons-material";
import FlagIcon from "@mui/icons-material/Flag";
import CloseIcon from "@mui/icons-material/Close";
import Table_theme from "../Themes/TableTheme";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { formatDateLocal, formatDateLocal_TF } from "../utils/dateUtils";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const salaryFields = [
  "employee_name", // Combined field for first_name and last_name
  "salary_id",
  "effective_from",
  "pan_no",
  "pf_uan_no",
  "bank_account_no",
  "insurance",
  "basic_salary",
  "hra",
  "conveyance",
  "special_allowance",
  "bonus",
  "pf_deduction",
  "tds",
  "gross_salary",
  "net_salary",
];

const popupFields = [
  "effective_from",
  "pan_no",
  "pf_uan_no",
  "bank_account_no",
  "insurance",
  "basic_salary",
  "hra",
  "conveyance",
  "special_allowance",
  "bonus",
  "pf_deduction",
  "tds",
  "gross_salary",
  "net_salary",
];
const dbDropdownFields = ["bo_id", "employee_id"];
const nonEditableFields = ["bo_id", "employee_id", "salary_id"];
const mandatoryFields = [
  "pan_no",
  "pf_uan_no",
  "bank_account_no",
  "effective_from",
  "basic_salary",
  "net_salary",
];
const fieldLabels = {};
salaryFields.forEach((field) => {
  fieldLabels[field] = field
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
});
fieldLabels.bo_id = "Organization Id";
fieldLabels.employee_name = "Employee Name";

const EmployeeSalaryDialog = ({ open, onClose, employeeId }) => {
  const { userId, empId, boId, roles, hasPermission } = useCurrentUser();
  const [fieldErrors, setFieldErrors] = useState({});
  const [data, setData] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [anchorEls, setAnchorEls] = useState({});
  const [alertMsg, setAlertMsg] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const initial = {};
    salaryFields.forEach((f) => (initial[f] = true));
    return initial;
  });
  const [newSalary, setNewSalary] = useState({
    delete_flag: "N",
    data_source: "UI",
    created_by: "system",
    created_on_date: new Date().toISOString().split("T")[0],
    employee_id: employeeId,
    bo_id: "",
    effective_from: "",
    pan_no: "",
    pf_uan_no: "",
    bank_account_no: "",
    insurance: "",
    basic_salary: "",
    hra: "",
    conveyance: "",
    special_allowance: "",
    bonus: "",
    pf_deduction: "",
    tds: "",
    gross_salary: "",
    net_salary: "",
  });

  const [editSalary, setEditSalary] = useState({});
  const [employeeBoId, setEmployeeBoId] = useState("");
  const [employeeName, setEmployeeName] = useState(""); // Store first_name + last_name
  const [activeForm, setActiveForm] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState(null);
  const confirmDelete = (salary) => {
    if (!salary || !salary.salary_id) {
      console.error("Invalid salary object");
      return;
    }
    setSelectedSalary(salary);
    setDeleteDialogOpen(true);
  };

  const fetchSalaries = useCallback(async () => {
    try {
      const [salaryResponse, employeeResponse] = await Promise.all([
        axios.get(`${BASE_URL}/employee_salaries/employee/${employeeId}`),
        axios.get(`${BASE_URL}/api/employees/${employeeId}/${boId}`),
      ]);
      const employeeData = employeeResponse.data;
      const fullName = `${employeeData.first_name || ""} ${
        employeeData.last_name || ""
      }`.trim();
      setEmployeeName(fullName || "Unknown Employee");

      if (salaryResponse.data.length === 0) {
        console.warn(`No salary records found for employee_id: ${employeeId}`);
        setSalaries([]);
      } else {
        // Merge first_name and last_name into a single employee_name field
        const enrichedSalaries = salaryResponse.data.map((salary) => ({
          ...salary,
          employee_name: fullName,
        }));
        setSalaries(enrichedSalaries);
      }
    } catch (err) {
      console.error("Error fetching salaries:", err);
      setAlertMsg(`Failed to fetch salaries: ${err.message}`);
      setAlertSeverity("error");
      setSalaries([]);
    }
  }, [employeeId]);

  const fetchEmployeeDetails = useCallback(async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/employees/${employeeId}/${boId}`
      );
      if (response.data && response.data.bo_id) {
        setEmployeeBoId(response.data.bo_id);
        setNewSalary((prev) => ({ ...prev, bo_id: response.data.bo_id }));
      } else {
        throw new Error("No bo_id found in employee data");
      }
    } catch (err) {
      console.error("Error fetching employee details:", err);
      setAlertMsg("Failed to fetch employee organization ID");
      setAlertSeverity("error");
    }
  }, [employeeId]);

  useEffect(() => {
    if (open && employeeId) {
      fetchEmployeeDetails();
      fetchSalaries();
    }
  }, [open, employeeId, fetchEmployeeDetails, fetchSalaries]);

  useEffect(() => {
    if (alertMsg) {
      const timer = setTimeout(() => {
        setAlertMsg("");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [alertMsg]);
  
  const validate = (salaryData) => {
    const errors = {};

    const requiredFields = [
      "employee_id",
      "effective_from",
      "pan_no",
      "pf_uan_no",
      "bank_account_no",
      "basic_salary",
      "net_salary",
    ];

    const numericFields = [
      "insurance",
      "basic_salary",
      "hra",
      "conveyance",
      "special_allowance",
      "bonus",
      "pf_deduction",
      "tds",
      "gross_salary",
      "net_salary",
    ];

    const MAX_VALUE = 99999999.99; // NUMERIC(10,2)

    // Required field validation
    requiredFields.forEach((field) => {
      if (
        salaryData[field] === undefined ||
        salaryData[field] === null ||
        String(salaryData[field]).trim() === ""
      ) {
        errors[field] = "This field is required";
      }
    });

    // Numeric validation
    numericFields.forEach((field) => {
      const value = salaryData[field];

      if (value === "" || value === null || value === undefined) return;

      const num = Number(value);

      if (Number.isNaN(num)) {
        errors[field] = "This field must be a valid number";
      } else if (num < 0) {
        errors[field] = "Negative values are not allowed";
      } else if (num > MAX_VALUE) {
        errors[field] = `Value exceeds allowed limit (${MAX_VALUE})`;
      }
    });

    // Date validation
    if (!salaryData.effective_from) {
      errors.effective_from = "This field is required";
    } else {
      const formatted = formatDateLocal_TF(salaryData.effective_from);
      if (!formatted) {
        errors.effective_from = "Invalid date format (use YYYY-MM-DD)";
      }
    }
    return errors;
  };

  const renderField = (field, state, setState) => {
    const isMandatory = mandatoryFields.includes(field);
    const label =
      (fieldLabels[field] || field) + (isMandatory ? " *" : " (Optional)");
    const handleChange = (e) => {
      if (!nonEditableFields.includes(field)) {
        const value = e.target.value;
        setState((prev) => ({ ...prev, [field]: value }));
        // Clear error if field has value
        if (
          mandatoryFields.includes(field) &&
          value != null &&
          value.toString().trim() !== ""
        ) {
          setFieldErrors((prev) => ({ ...prev, [field]: "" }));
        }
      }
    };

    if (field === "effective_from") {
      return (
        <TextField
          key={field}
          label={label}
          value={state[field] ? state[field] : ""}
          onChange={(e) => {
            if (!nonEditableFields.includes(field)) {
              const value = e.target.value;
              setState((prev) => ({ ...prev, [field]: value }));
              if (
                mandatoryFields.includes(field) &&
                value != null &&
                value.toString().trim() !== ""
              ) {
                setFieldErrors((prev) => ({ ...prev, [field]: "" }));
              }
            }
          }}
          sx={{ width: "180px" }}
          size="small"
          type="date"
          error={!!fieldErrors[field]}
          helperText={fieldErrors[field] || ""}
          InputProps={{
            sx: { fontSize: "0.85rem" },
            readOnly: nonEditableFields.includes(field),
          }}
          InputLabelProps={{
            shrink: true,
            sx: { fontSize: "1.0rem", mt: -0.15 },
          }}
        />
      );
    }

    return (
      <TextField
        key={field}
        label={label}
        value={state[field] || ""}
        onChange={(e) => {
          if (!nonEditableFields.includes(field)) {
            const value = e.target.value;
            setState((prev) => ({ ...prev, [field]: value }));
            if (
              mandatoryFields.includes(field) &&
              value != null &&
              value.toString().trim() !== ""
            ) {
              setFieldErrors((prev) => ({ ...prev, [field]: "" }));
            }
          }
        }}
        sx={{ width: "180px" }}
        size="small"
        error={!!fieldErrors[field]}
        helperText={fieldErrors[field] || ""}
        InputProps={{
          sx: { fontSize: "0.85rem" },
          readOnly: nonEditableFields.includes(field),
        }}
        InputLabelProps={{
          shrink: true,
          sx: { fontSize: "1.0rem", mt: -0.15 },
        }}
      />
    );
  };

  const handleAddSalary = async () => {
    const errors = validate(newSalary);
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return; // stop submission
    }
    try {
      const { salary_id, ...payload } = {
        ...newSalary,
        bo_id: boId,
        employee_id: employeeId,
        created_by: empId || "system",
        modified_by: empId || "system",
      };
      await axios.post(`${BASE_URL}/employee_salaries`, payload);
      setNewSalary((prev) => ({
        ...prev,
        effective_from: "",
        pan_no: "",
        pf_uan_no: "",
        bank_account_no: "",
        insurance: "",
        basic_salary: "",
        hra: "",
        conveyance: "",
        special_allowance: "",
        bonus: "",
        pf_deduction: "",
        tds: "",
        gross_salary: "",
        net_salary: "",
      }));
      setAlertMsg("Salary created successfully!");
      setAlertSeverity("success");
      fetchSalaries();
      setActiveForm(null);
    } catch (err) {
      console.error("Add failed:", err);
      let errorMessage = "Failed to add record";
      //  Read backend message safely
      if (err.response && err.response.data) {
        errorMessage =
          err.response.data.message || err.response.data.error || errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setAlertMsg(errorMessage); // clean backend message
      setAlertSeverity("error");
    }
  };

  const handleEditSalary = (salary) => {
    setEditSalary({
      ...salary,
      effective_from: salary.effective_from
        ? formatDateLocal_TF(salary.effective_from)
        : "",
    });
    setActiveForm("edit");
  };

  const handleDeleteSalary = async (salaryId) => {
    const payload = {
      delete_flag: "Y",
      modified_by: empId,
    };
    try {
      await axios.delete(`${BASE_URL}/employee_salaries/${boId}/${salaryId}`);
      setAlertMsg("Salary deleted successfully..");
      setAlertSeverity("success");
      fetchSalaries();
    } catch (err) {
      console.error("Delete failed:", err);
      setAlertMsg(`Failed to delete record: ${err.message}`);
      setAlertSeverity("error");
    }
  };

  const handleUpdateSalary = async () => {
    const errors = validate(editSalary);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    try {
      const payload = {
        ...editSalary,
        modified_by: empId,
      };
      await axios.put(
        `${BASE_URL}/employee_salaries/${boId}/${editSalary.salary_id}`,
        payload
      );
      setEditSalary({});
      setAlertMsg("Salary updated successfully!");
      setAlertSeverity("success");
      fetchSalaries();
      setActiveForm(null);
    } catch (err) {
      console.error("Edit failed:", err);
      setAlertMsg(`Failed to update record: ${err.message}`);
      setAlertSeverity("error");
    }
  };

  const handleCancel = () => {
    setEditSalary({});
    setActiveForm(null);
  };

  const handleAddButtonClick = () => {
    setActiveForm("add");
  };
  const handleToggleFlag = async (salary_id, currentFlag) => {
    const newFlag = currentFlag === "N" ? "Y" : "N";
    try {
      const response = await fetch(
        `${BASE_URL}/employee_salaries/${boId}/${salary_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bo_id: boId,
            delete_flag: newFlag,
            modified_by: empId,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Flag update failed");
      fetchSalaries();
      setAlertMsg("Toggle flag updated successfully..");
      setAlertSeverity("success");
    } catch (err) {
      console.error("Error updating flag:", err);
      setAlertMsg(`Failed to update flag: ${err.message}`);
      setAlertSeverity("error");
    }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          borderRadius: "12px",
          background: "linear-gradient(45deg, #e6f0fa, #ffffff)",
          p: 2,
          fontSize: "14px",
          fontWeight: 550,
        }}
      >
        Salary Details for {employeeName || `Employee ID: ${employeeId}`}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 5 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{}}>
        {!activeForm && (
          <Box mb={2} display="flex" justifyContent="flex-end">
            {hasPermission("Employee Salaries", "Edit") && (
            <Button
              variant="contained"
              onClick={handleAddButtonClick}
              sx={{
                mt: 1,
              }}
            >
              Add Salary
            </Button>
           )} 
          </Box>
        )}
        {activeForm === "add" && (
          <Box mb={2}>
            <Grid container spacing={1} mt={2}>
              {popupFields.map((field) => (
                <Grid item xs={12} sm={6} key={field}>
                  {renderField(field, newSalary, setNewSalary)}
                </Grid>
              ))}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleAddSalary}
                  sx={{
                    mt: 1,
                    px: 2.5,
                    height: 36,
                    backgroundColor: "#2db0e4",
                    color: "#fff",
                    textTransform: "none",
                    fontSize: "0.85rem",
                    mr: 1,
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                    onClick={() => {
                    setActiveForm(null);
                    setFieldErrors({});
                  }}
                  sx={{
                    mt: 1,
                    textTransform: "none",
                    fontSize: "0.85rem",
                    height: 36,
                    px: 2.5,
                  }}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
        {activeForm === "edit" && (
          <Box mb={2}>
            <Grid container spacing={1} mt={2}>
              {popupFields.map((field) => (
                <Grid item xs={12} sm={6} key={field}>
                  {renderField(field, editSalary, setEditSalary)}
                </Grid>
              ))}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleUpdateSalary}
                  sx={{
                    mt: 1,
                    textTransform: "none",
                    fontSize: "0.85rem",
                    height: 36,
                    px: 2.5,
                    mr: 1,
                  }}
                >
                  Update
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.85rem",
                    height: 36,
                    px: 2.5,
                    mt: 1,
                  }}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
        {alertMsg && (
          <Box mb={2}>
            <Alert
              onClose={() => setAlertMsg("")}
              variant="standard"
              sx={{
                color: alertSeverity === "success" ? "#2E9F50" : "#FF0C0C",
                fontWeight: 500,
                backgroundColor: "#fff",
              }}
            >
              {alertMsg}
            </Alert>
          </Box>
        )}
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{}}>Actions</TableCell>
                  {salaryFields.map((field) => (
                    <TableCell key={field} sx={{}}>
                      {fieldLabels[field] || field}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {salaries.length > 0 ? (
                  salaries.map((salary) => (
                    <TableRow key={salary.salary_id}>
                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          <Tooltip
                            title={
                              hasPermission("Employee Salaries", "Edit")
                                ? "Edit"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission("Employee Salaries", "Edit") &&
                                  handleEditSalary(salary)
                                }
                                disabled={
                                  !hasPermission("Employee Salaries", "Edit")
                                }
                                sx={{
                                  color: hasPermission(
                                    "Employee Salaries",
                                    "Edit"
                                  )
                                    ? "#2DB0E4"
                                    : "#ccc",
                                }}
                              >
                                <EditOutlinedIcon sx={{ fontSize: "16px" }} />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip
                            title={
                              hasPermission("Employee Salaries", "Delete")
                                ? "Delete"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission(
                                    "Employee Salaries",
                                    "Delete"
                                  ) && confirmDelete(salary)
                                }
                                disabled={
                                  !hasPermission("Employee Salaries", "Delete")
                                }
                              >
                                <DeleteIcon
                                  sx={{
                                    color: hasPermission(
                                      "Employee Salaries",
                                      "Delete"
                                    )
                                      ? "red"
                                      : "#ccc",
                                    width: 15,
                                    height: 15,
                                  }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip
                            title={
                              hasPermission("Employee Salaries", "Edit")
                                ? "Toggle Delete Flag"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission("Employee Salaries", "Edit") &&
                                  handleToggleFlag(
                                    salary.salary_id,
                                    salary.delete_flag
                                  )
                                }
                                disabled={
                                  !hasPermission("Employee Salaries", "Edit")
                                }
                              >
                                <FlagIcon
                                  style={{
                                    color:
                                      salary.delete_flag === "N"
                                        ? "#2E9F50"
                                        : "#FF0C0C",
                                    transition: "color 0.2s",
                                    width: 17,
                                    height: 17,
                                    verticalAlign: "middle",
                                  }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {salaryFields.map((field) => (
                        <TableCell key={field} sx={{}}>
                          {field === "effective_from" && salary[field]
                            ? formatDateLocal(salary[field])
                            : field === "employee_name"
                            ? salary[field] || "Unknown Employee"
                            : salary[field] || ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={salaryFields.length + 1}
                      align="center"
                      sx={{}}
                    >
                      No salary records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </ThemeProvider>
      </DialogContent>
      <DialogActions
        sx={{
          p: "8px 24px",
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          sx={{
            mr: 1,
            color: "#555",
            borderColor: "#ccc",
          }}
        >
          Close
        </Button>
      </DialogActions>
      {getConfirmDeleteDialog({
        open: deleteDialogOpen,
        onClose: () => {
          setDeleteDialogOpen(false);
          setSelectedSalary(null);
        },
        onConfirm: () => {
          if (selectedSalary) {
            handleDeleteSalary(selectedSalary.salary_id);
          }
          setDeleteDialogOpen(false);
          setSelectedSalary(null);
        },
        title: "Confirm Deletion",
        contentText: selectedSalary
          ? `Are you sure you want to delete the salary for ${selectedSalary.employee_name}?`
          : "Are you sure you want to delete this Salary?",
      })}
    </Dialog>
  );
};

export default EmployeeSalaryDialog;
